// Team Excel workbooks — Salespersons, Supervisors, and one supervisor's file.
//
// Every download here answers for EXACTLY the window set on the screen: the
// caller passes the same rows the table is showing plus a window label, and
// the label is stamped on every sheet so nobody mistakes "today's file" for
// the all-time one.

import * as XLSX from 'xlsx';

// Sheets size their own columns — a report nobody has to resize to read.
const aoa = (rows) => {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const widths = [];
  rows.forEach((r) => (r || []).forEach((cell, i) => {
    const len = String(cell ?? '').length;
    if (!widths[i] || len > widths[i]) widths[i] = len;
  }));
  ws['!cols'] = widths.map((w) => ({ wch: Math.max(6, Math.min(42, (w || 0) + 2)) }));
  return ws;
};
const n = (v) => Number(v || 0);

const save = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  sheets.forEach(([name, ws]) => XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31)));
  XLSX.writeFile(wb, filename);
};

const slugify = (s) => String(s || 'window').toLowerCase()
  .replace(/[^\w→-]+/g, '_').replace(/→/g, 'to').replace(/^_|_$/g, '');

// ─── Salespersons — the whole team for one window ────────────────────────────

export function downloadSalespersonsWorkbook({ rows, team, windowLabel }) {
  const label = windowLabel || 'All time';
  const summary = ['Summary', aoa([
    ['SALESPERSONS REPORT'],
    ['Window', label],
    [],
    ['Salespersons', rows.length],
    ['Inquiries', team.inquiries ?? 0],
    ['Deals sold', team.deals ?? 0],
    ['Turnover', n(team.turnover)],
    ['Balance to collect', n(team.balance)],
    ['Lost', team.lost ?? 0],
  ])];

  const header = ['#', 'Salesperson', 'Phone', 'Email', 'Active',
    'Inquiries', 'New', 'Turnover', 'Deals', 'Sold', 'Lost', 'Balance to collect'];
  const body = rows.map((s, i) => [
    i + 1, s.name, s.phone_number || '', s.email || '', s.is_active ? 'Yes' : 'No',
    s.inquiry_stats?.total ?? 0, s.inquiry_stats?.new ?? 0,
    n(s.turnover), s.deals ?? 0, s.sold ?? 0, s.lost ?? 0, n(s.balance_due),
  ]);
  const sum = (f) => rows.reduce((a, s) => a + n(f(s)), 0);
  const people = ['Salespersons', aoa([
    [`Salespersons — ${label}`],
    ['Every number is measured over this window. People are never hidden — an empty window just zeroes them.'],
    [],
    header,
    ...body,
    [],
    ['TOTAL', '', '', '', '',
      sum((s) => s.inquiry_stats?.total), sum((s) => s.inquiry_stats?.new),
      sum((s) => s.turnover), sum((s) => s.deals), sum((s) => s.sold),
      sum((s) => s.lost), sum((s) => s.balance_due)],
  ])];

  save([summary, people], `salespersons_${slugify(label)}.xlsx`);
}

// ─── Supervisors — the scoreboard for one window ─────────────────────────────

export function downloadSupervisorsWorkbook({ rows, team, windowLabel }) {
  const label = windowLabel || 'Today';
  const summary = ['Summary', aoa([
    ['SUPERVISORS REPORT'],
    ['Window', label],
    [],
    ['Supervisors', rows.length],
    ['Inquiries in', team.inquiries ?? 0],
    ['Quoted', team.quoted ?? 0],
    ['Reply rate %', team.replyPct ?? 0],
    ['Still waiting', team.pending ?? 0],
  ])];

  const header = ['#', 'Supervisor', 'Phone', 'Email', 'Active',
    'Quoted', 'Share of all in %', 'Corrected', 'Sold', 'Sold value'];
  const sum = (f) => rows.reduce((a, s) => a + n(f(s)), 0);
  const board = ['Supervisors', aoa([
    [`Supervisors — ${label}`],
    ['"Share of all in" is quoted ÷ every inquiry that came in — unhandled work stays visible.'],
    [],
    header,
    ...rows.map((s, i) => [
      i + 1, s.name, s.phone_number || '', s.email || '', s.is_active ? 'Yes' : 'No',
      s.quoted ?? 0, s.handled_pct ?? 0, s.edited ?? 0, s.sold ?? 0, n(s.sold_value),
    ]),
    [],
    ['TOTAL', '', '', '', '', sum((s) => s.quoted), '', sum((s) => s.edited),
      sum((s) => s.sold), sum((s) => s.sold_value)],
  ])];

  save([summary, board], `supervisors_${slugify(label)}.xlsx`);
}

// ─── One supervisor — their full file for one window ─────────────────────────

const safeName = (s) => (s || 'supervisor').replace(/[^\w]+/g, '_').replace(/^_|_$/g, '');

export function downloadSupervisorWorkbook(data) {
  const sup = data?.supervisor || {};
  const s = data?.summary || {};
  const w = data?.window || {};
  const label = w.period === 'custom'
    ? `${w.from || 'the beginning'} → ${w.to || ''}`
    : { today: 'Today', yesterday: 'Yesterday', week: 'This week',
        month: 'This month', year: 'This year', all: 'All time' }[w.period] || w.period;

  const summary = ['Summary', aoa([
    ['SUPERVISOR FILE'],
    ['Name', sup.name || ''],
    ['Phone', sup.phone_number || ''],
    ['Email', sup.email || ''],
    ['Joined', sup.joined || ''],
    ['Generated', data?.generated_at || ''],
    [],
    [`— ${label} —`],
    ['Inquiries that came in (whole team)', s.window_inquiries ?? 0],
    ['Quoted by anyone', s.window_quoted ?? 0],
    ['Team reply rate %', s.reply_pct ?? 0],
    [],
    ['Quoted by THIS supervisor', s.quoted ?? 0],
    ['Share of everything in %', s.handled_pct ?? 0],
    ['Quotes corrected', s.edited ?? 0],
    ['Sold', s.sold ?? 0],
    ['Sold value', n(s.sold_value)],
    ['Average minutes to quote', s.avg_minutes_to_quote ?? '—'],
  ])];

  const inquiries = ['Inquiries', aoa([
    [`Every deal ${sup.name || ''} priced or corrected — ${label}`],
    [],
    ['Inquiry #', 'Came in', 'Quoted at', 'Minutes to quote', 'Role', 'Stage',
      'Customer', 'Phone', 'Salesperson', 'Car', 'Parts', 'Qty of parts',
      'First quote', 'Quoted price', 'Corrected by', 'Final amount',
      'Sold', 'Sold value', 'Payment confirmed', 'Supervisor notes'],
    ...(data?.inquiries || []).map((r) => [
      r.inquiry_id, r.created_at, r.quoted_at, r.minutes_to_quote ?? '',
      r.role, r.stage, r.customer, r.customer_phone, r.salesperson, r.car,
      r.parts, r.parts_count, r.first_quoted_price, r.quoted_price,
      r.corrected_by, r.final_amount, r.sold ? 'Yes' : '', r.sold_value,
      r.payment_confirmed_at, r.supervisor_notes]),
  ])];

  const parts = ['Parts', aoa([
    [`Every part on those deals — ${label}`],
    [],
    ['Inquiry #', 'Date', 'Customer', 'Car', 'Part', 'Qty', 'Source', 'Price',
      'Priced by', 'Corrected by', 'Stage'],
    ...(data?.parts || []).map((r) => [r.inquiry_id, r.date, r.customer, r.car,
      r.part, r.quantity, r.source, r.price, r.priced_by, r.corrected_by, r.stage]),
  ])];

  const daily = ['Day by day', aoa([
    [`Their work, one row per day — ${label}`],
    [],
    ['Date', 'Deals touched', 'Priced', 'Corrected', 'Sold', 'Sold value'],
    ...(data?.daily || []).map((d) => [d.date, d.touched, d.priced, d.corrected,
      d.sold, n(d.sold_value)]),
  ])];

  save([summary, inquiries, parts, daily],
       `supervisor_${safeName(sup.name)}_${slugify(label)}.xlsx`);
}
